import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, Switch, TextField, FormControlLabel, FormControl, InputLabel, Select, OutlinedInput, MenuItem, FormHelperText, InputAdornment, IconButton } from '@mui/material';
import { Container, MainContainer } from '../../../../components';
import { UserCreationMail } from '../UserCreationMail/UserCreationMail';
import { Menu } from '../Menu/Menu';
import moduleTypes from '../../../../constant/moduleTypes';
import { useToaster } from '../../../../providers/ToasterProvider';

import { createUser, getById, updateById } from '../../../../services/usersService';

import RadiusButton from '../../../../components/RadiusButton';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg'
import BACK from '../../../../assets/images/back.svg';

import './UserDetails.scss';
import { LoadingButton } from '@mui/lab';
import { ProjectContext } from '../../../../providers/projectProvider';
import patterns from '../../../../constant/patterns';

export const UserDetails = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    const [modules, setModules] = useState([]);
    const [modulesError, setModulesError] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [mailModal, setMailModal] = useState({
        isUpdated: false,
        isOpen: false,
        cancelPressed: false
    });
    const [isActive, setIsActive] = useState(true);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const userSchema = z.object({
        firstName: z.string().min(1, { message: 'First name is required.' }),
        lastName: z.string().min(1, { message: 'Last name is required.' }),
        username: z.string().min(1, { message: 'User name is required.' }),
        mobile: z.string().regex(new RegExp('^[0-9]*$'), { message: 'Please enter valid mobile number.' }).length(10, { message: 'Please enter valid mobile number.' }),
        email: z.string().min(1, { message: 'Email id is required' }).email('Please enter valid email address.'),
        password: z.string().optional(),
    }).superRefine(({ password, username }, ctx) => {
        if ((editMode && username && (username.length < 8 || username.length > 20)) || (!editMode && (username.length < 8 || username.length > 20))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username should be at least 8 characters long and shorter than 20 characters',
                path: ['username']
            });
        }
        if ((userDetails && username && !patterns.USERNAME.test(username)) || (!userDetails && !patterns.USERNAME.test(username))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username must contain only alphanumeric characters, underscores, periods, or hyphens',
                path: ['username']
            });
        }
        if ((userDetails && password && password.length < 8) || (!userDetails && password.length < 8)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password should be at least 8 characters long',
                path: ['password']
            });
        }
        if ((userDetails && password && !patterns.PASSWORD.test(password)) || (!userDetails && !patterns.PASSWORD.test(password))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password must contain one uppercase character, one lowercase character, one digit and one special character',
                path: ['password']
            });
        }
    });
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        setValue,
        reset,
    } = useForm({
        resolver: zodResolver(userSchema),
        // defaultValues: {
        //     firstName: 'jayesh',
        //     lastName: 'vora',
        //     mobile: '8888888888',
        //     email: 'jayesh@mailinator.com',
        //     password: 'test@123'
        // }
    });

    useEffect(() => {
        if (location?.state?.userId) {
            const fetchUserDetails = async () => {
                try {
                    const res = await getById(location.state.userId);
                    if (res.code === 'OK') {
                        setEditMode(true);
                        const userData = res.data;
                        setValue('firstName', userData.firstName);
                        setValue('lastName', userData.lastName);
                        setValue('username', userData.username);
                        setValue('mobile', userData.mobile);
                        setValue('email', userData.email);
                        // setValue('password', userData.password);
                        const assignedModules = userData.modules.map((m) => +m);
                        const tags = moduleTypes.filter((module) => assignedModules.includes(module.code)).map((m) => m.displayKey);
                        setModules(tags);
                        setIsActive(userData.isActive);
                        setUserDetails(userData);
                    }
                } catch (error) {
                    console.log('There is an error while getting user details', error);
                }
            }
            fetchUserDetails();
        }
    }, [location, setValue, setModules]);

    useEffect(() => {
        if (mailModal.cancelPressed) {
            const path = location.pathname.replace('/user-details', '');
            navigate(path);
        }
    }, [mailModal, navigate, location]);

    const submitForm = async (values) => {
        if (modules.length === 0) {
            setModulesError(true);
            return;
        }

        const body = {
            ...values,
            isActive: isActive,
            modules: moduleTypes.filter((module) => modules.includes(module.displayKey)).map((m) => m.code)
        }
        console.log('body', body);
        if (editMode) {
            if (!userDetails?.id) return;
            setIsLoading(true);
            if (!body.password) {
                delete body.password;
            }
            try {
                const res = await updateById(userDetails.id, body);
                if (res?.code === 'OK') {
                    addNotification('User updated successfully.', 'success');
                    if (body.password) {
                        setUserDetails({
                            ...res.data.user,
                            password: values.password
                        })
                        setMailModal({
                            ...mailModal,
                            isUpdated: true,
                            isOpen: true,
                        });
                    } else {
                        onGoBack();
                    }
                }
            } catch (error) {
                console.log('There is an error while updating user', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsLoading(true);
            try {
                body.userType = 3;
                const res = await createUser(body);
                if (res?.code === 'CREATED') {
                    const data = {
                        ...res.data.user,
                        password: values.password
                    }
                    setUserDetails(data);
                    setMailModal({
                        ...mailModal,
                        isUpdated: false,
                        isOpen: true,
                    })
                    addNotification('User created successfully.', 'success');
                    reset();
                }
            } catch (error) {
                if (error?.response?.data?.data?.code === 'E_DUPLICATE') {
                    addNotification('User name is taken.', 'danger');
                }
                console.log('There is an error while creating user', error);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const onGoBack = () => {
        const path = location.pathname.replace('/user-details', '');
        navigate(path);
    }

    const handleChange = (event) => {
        if (modulesError) {
            setModulesError(false);
        }
        const value = event.target.value;
        setModules(value);
    };

    const handleActiveChange = () => {
        setIsActive(!isActive);
    }

    return (
        <MainContainer sx={{ bgcolor: 'red' }}>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        {editMode ? 'Edit User' : 'Create User'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>

                <Typography component='h5' variant='h5' sx={{
                    mb: 3
                }}>
                    {editMode ? 'Edit User' : 'Create User'}
                </Typography>

                <Box sx={{
                    bgcolor: 'background.base',
                    borderRadius: 6,
                    p: 4
                }} className='create-user-form-wrapper'>
                    <Box component='form' autoComplete='off' onSubmit={handleSubmit(submitForm)}>
                        <div className="user-form-details">

                            <TextField
                                id='firstName'
                                name='firstName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>First Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('firstName')}
                                error={Boolean(errors.firstName)}
                                helperText={errors.firstName?.message}
                                onBlur={() => trigger('firstName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='lastName'
                                name='lastName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Last Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('lastName')}
                                error={Boolean(errors.lastName)}
                                helperText={errors.lastName?.message}
                                onBlur={() => trigger('lastName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='username'
                                name='username'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>User Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('username')}
                                error={Boolean(errors.username)}
                                helperText={errors.username?.message}
                                onBlur={() => trigger('username')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <FormControl variant='outlined' sx={{ mb: 2 }}>
                                    <InputLabel htmlFor="passwordLabel">
                                        <Box component='span'>Password</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </InputLabel>
                                    <OutlinedInput
                                        id='passwordLabel'
                                        type={passwordVisible ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                                    edge="end"
                                                >
                                                    <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label="Password"
                                        name="password"
                                        {...register('password')}
                                        error={Boolean(errors.password)}
                                        onBlur={() => trigger('password')}
                                    />
                                    {errors?.password?.message && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.password?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {userDetails?.id && <FormHelperText sx={{ mt: '-10px', mb: 1, color: 'primary.main' }}>
                                    Enter password only if you wish to update.
                                </FormHelperText>}
                            </Box>

                            <TextField
                                id='email'
                                name='email'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Email Id</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('email')}
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                                onBlur={() => trigger('email')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <TextField
                                id='mobile'
                                name='mobile'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Mobile No</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('mobile')}
                                error={Boolean(errors.mobile)}
                                helperText={errors.mobile?.message}
                                onBlur={() => trigger('mobile')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <FormControl>
                                <InputLabel id="createUserModulesLabel">
                                    <Box component='span'>Module</Box>
                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                </InputLabel>
                                <Select
                                    labelId="createUserModulesLabel"
                                    id="module"
                                    multiple
                                    value={modules}
                                    onChange={handleChange}
                                    input={
                                        <OutlinedInput id="select-multiple-chip" label='Module' />
                                    }
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {selected.map((value) => (
                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    bgcolor: 'primary.50',
                                                    py: 0.5,
                                                    px: 1.5,
                                                    borderRadius: 1
                                                }} key={value}>
                                                    <span>{value}</span>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {moduleTypes.map((module) => (
                                        <MenuItem
                                            key={module.code}
                                            value={module.displayKey}
                                        >
                                            {module.displayKey}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {modulesError && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        Please assign at least one module
                                    </FormHelperText>
                                )}

                            </FormControl>

                        </div>

                        <FormControlLabel sx={{
                            mt: 2,
                            mb: 3
                        }} control={
                            <Switch
                                checked={isActive}
                                onChange={handleActiveChange}
                            />
                        } label='Active' />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <RadiusButton sx={{
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }} color='grey'
                                variant='contained'
                                size='large'
                                // disabled
                                onClick={onGoBack}
                                startIcon={<img src={BACK} alt='' />}>
                                Back
                            </RadiusButton>
                            <LoadingButton sx={{
                                textTransform: 'uppercase',
                                borderRadius: 40,
                            }} color='primary'
                                type="submit"
                                variant='contained'
                                size='large'
                                loading={isLoading}
                            >
                                Submit
                            </LoadingButton>
                        </Box>


                    </Box>
                </Box>

            </Container>
            <UserCreationMail userDetails={userDetails} openState={mailModal.isOpen} isUpdated={mailModal.isUpdated} setMailModal={setMailModal} />
        </MainContainer >
    );
}
